import { createStyles, FormControl, FormControlLabel, IconButton, makeStyles, Radio, RadioGroup, Theme } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { ChartCommonProps, IconButtonWrapper } from '../Common';
import { ChartFilter } from '../ChartComponents';
import { SearchCriteria } from '../SearchCriteria';
import CloseIcon from '@material-ui/icons/Close';
import { convertStringNumberToNumber, formatNumber, getLabels, getSuggestedMaxTick } from '../../helpers/ChartHelper';
import gql from 'graphql-tag';
import { useQuery } from 'react-apollo';
import { DrillDownType } from '../DrillDownType';
import { SelectedItemProps } from '../MattersByStatusChart';
// import moment from 'moment';

import { Bar, getElementAtEvent } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, Scale, Tick, Chart, ChartEvent, ActiveElement } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import { ChartInfo } from '../../components/ChartInfo';
import VideoLibraryOutlinedIcon from '@material-ui/icons/VideoLibraryOutlined';
import { VideoModal } from '../../components/VideoModal';
import MTooltip from '@material-ui/core/Tooltip';
import { ForecastFeesBilledChart } from './ForecastFeesBilledChart';

import { RV_CHART_CLASS, RV_CHART_COLORS, isChartDataEmpty, rvBarDatasetStyle, rvChartAnimation, rvCategoryAxisStyle, rvTooltipStyle, rvValueAxisStyle } from '../chartTheme';
import { ChartHeading } from '../ChartHeading';
import { ChartLimitNote } from '../ChartLimitNote';
import { ChartSkeleton } from '../../components/loading/ChartSkeleton';
import { AnimatedNumber } from '../AnimatedNumber';
import { ChartEmptyState } from '../ChartEmptyState';
import TimelineOutlinedIcon from '@material-ui/icons/TimelineOutlined';
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, zoomPlugin);

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        root: {
            position: 'relative',
            paddingBottom: '10px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            maxWidth: '100%',
            maxHeight: '100%',
            height: '100%',
            textAlign: 'center',
            '@media print': {
                display: 'flex',
                flexFlow: 'column',
                alignItems: 'center',
                boxShadow: 'none !important',
            },
        },
        popover: {},
        paper: {},
        radioGroup: {
            flexFlow: 'row wrap',
            justifyContent: 'center',
        },
        closeIcon: {
            color: 'rgb(0, 120, 212)',
        },
        titleContainer: {
            display: 'flex',
            flexFlow: 'column',
            padding: '20px 15px 0px 15px',
            '& .title': {
                fontSize: 16,
            },
        },
        barContainer: {
            padding: '5px 15px 15px 15px',
            minHeight: '300px',
            flex: 1,
            display: 'flex',
            flexFlow: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            maxHeight: '350px',
            maxWidth: 'auto',
            position: 'relative',
            '@media (max-width: 533px)': {
                maxWidth: '350px',
                margin: '0 auto',
            },
            '& .titleWrapper': {
                textAlign: 'center',
                display: 'inline-block',
                width: '100%',
                padding: '15px',
                '& .title': {
                    fontSize: '18px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .value': {
                    fontSize: '24px',
                    fontWeight: 600,
                    display: 'block',
                    marginBottom: '15px',
                },
                '& .secondary-title': {
                    fontSize: '16px',
                    fontWeight: 400,
                    display: 'block',
                    marginBottom: '15px',
                },
            },
        },
        loadingWrapper: {
            padding: '20px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%',
        },
        title: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
        },
        secondTitleDrilldown: {
            fontSize: 16,
            fontWeight: 600,
            fontFamily: 'inherit',
            marginBottom: 0,
            cursor: 'pointer',
            userSelect: 'none',
        },
        totalDiv: {
            cursor: 'pointer',
            paddingRight: 5,
        },
        helpIcon: {
            position: 'absolute',
            left: '5px',
            top: '5px',
            '@media print': {
                display: 'none !important',
            },
        },
        total: {
            fontSize: '2.5rem',
            padding: '10px 0',
            fontWeight: 600,
            cursor: 'pointer',
            userSelect: 'none',
        },
        matterSettled: {
            fontSize: '1rem',
            margin: '10px 0px',
            cursor: 'pointer',
            userSelect: 'none',
        },
        footer: {
            fontSize: '1rem',
            textAlign: 'center',
        },
        learnIcon: {
            color: 'rgb(0, 120, 212)'
        },
    }),
);

export enum PeriodEnum {
    MONTH,
    PREV_MONTH,
    NEXT_MONTH,
    NEXT_6_MONTHS,
    NEXT_12_MONTHS,
    NEXT_3_YEARS,
}

interface ForecastFeesBilledByCategoryChartProps extends SelectedItemProps, ChartCommonProps {
    chartFilter?: ChartFilter;
    infoUrl: string | null;
}

interface ChartState {
    last: PeriodEnum;
    grouping: string[];
    fees: number[];
    format: string[];
    total: number;
    symbol: string;
}

export const ForecastFeesBilledByCategoryChart: React.FC<ForecastFeesBilledByCategoryChartProps> = (props) => {
    const classes = useStyles();

    // tslint:disable-next-line:no-any
    const chartRef = React.useRef<any>();

    const [isVideoModalOpen, setIsVideoModalOpen] = useState<boolean>(false);

    // tslint:disable-next-line:no-any
    const { loading, error, data } = useQuery<ForecastFeesBilledByCategoryChartData>(ForecastFeesBilledChartQuery, {
        variables: {
            entityType: props.chartFilter?.entityType,
            entityGuid: props.chartFilter?.entityGuid,
            responsibility: props.chartFilter?.responsibility,
        },
    });

    const [state, setState] = useState<ChartState>({
        last: PeriodEnum.NEXT_6_MONTHS,
        grouping: [],
        fees: [],
        format: [],
        total: 0,
        symbol: '$',
    });

    const currencySymbolQuery = useQuery<CurrencySymbolData>(CURRENCY_SYMBOL_QUERY);

    useEffect(() => {
        if (currencySymbolQuery.data && currencySymbolQuery.data.options && !currencySymbolQuery.loading && !currencySymbolQuery.error) {
            setState((prevState) => {
                return {
                    ...prevState,
                    symbol: currencySymbolQuery.data!.options.currencySymbol.symbol,
                };
            });
        }
        // tslint:disable-next-line: align
    }, [currencySymbolQuery.loading, currencySymbolQuery.error, currencySymbolQuery.data]);

    useEffect(() => {
        if (!loading) {
            setState((prevState) => {
                let fees: number[] = [];
                let grouping: string[] = [];
                let total: number = 0;
                let format: string[] = [];

                if (!error && data && data.statistics && data.statistics.matter && data.statistics.matter.forecastFeesBilledByCategory) {
                    if (state.last === PeriodEnum.PREV_MONTH) {
                        fees = data.statistics.matter.forecastFeesBilledByCategory.previousMonth.forecastFeesBilledGrouping.map((s) => s.fees);
                        grouping = data.statistics.matter.forecastFeesBilledByCategory.previousMonth.forecastFeesBilledGrouping.map((s) => s.grouping);
                        format = data.statistics.matter.forecastFeesBilledByCategory.previousMonth.forecastFeesBilledGrouping.map((s) => s.format);
                        total = data.statistics.matter.forecastFeesBilledByCategory.previousMonth.total;

                    } else if (state.last === PeriodEnum.MONTH) {
                        fees = data.statistics.matter.forecastFeesBilledByCategory.currentMonth.forecastFeesBilledGrouping.map((s) => s.fees);
                        grouping = data.statistics.matter.forecastFeesBilledByCategory.currentMonth.forecastFeesBilledGrouping.map((s) => s.grouping);
                        format = data.statistics.matter.forecastFeesBilledByCategory.currentMonth.forecastFeesBilledGrouping.map((s) => s.format);
                        total = data.statistics.matter.forecastFeesBilledByCategory.currentMonth.total;
                        
                    } else if (state.last === PeriodEnum.NEXT_MONTH) {
                        fees = data.statistics.matter.forecastFeesBilledByCategory.nextMonth.forecastFeesBilledGrouping.map((s) => s.fees);
                        grouping = data.statistics.matter.forecastFeesBilledByCategory.nextMonth.forecastFeesBilledGrouping.map((s) => s.grouping);
                        format = data.statistics.matter.forecastFeesBilledByCategory.nextMonth.forecastFeesBilledGrouping.map((s) => s.format);
                        total = data.statistics.matter.forecastFeesBilledByCategory.nextMonth.total;

                    } else if (state.last === PeriodEnum.NEXT_6_MONTHS) {
                        fees = data.statistics.matter.forecastFeesBilledByCategory.nextSixMonths.forecastFeesBilledGrouping.map((s) => s.fees);
                        grouping = data.statistics.matter.forecastFeesBilledByCategory.nextSixMonths.forecastFeesBilledGrouping.map((s) => s.grouping);
                        format = data.statistics.matter.forecastFeesBilledByCategory.nextSixMonths.forecastFeesBilledGrouping.map((s) => s.format);
                        total = data.statistics.matter.forecastFeesBilledByCategory.nextSixMonths.total;

                    } else if (state.last === PeriodEnum.NEXT_12_MONTHS) {
                        fees = data.statistics.matter.forecastFeesBilledByCategory.nextTwelveMonths.forecastFeesBilledGrouping.map((s) => s.fees);
                        grouping = data.statistics.matter.forecastFeesBilledByCategory.nextTwelveMonths.forecastFeesBilledGrouping.map((s) => s.grouping);
                        format = data.statistics.matter.forecastFeesBilledByCategory.nextTwelveMonths.forecastFeesBilledGrouping.map((s) => s.format);
                        total = data.statistics.matter.forecastFeesBilledByCategory.nextTwelveMonths.total;

                    } else if (state.last === PeriodEnum.NEXT_3_YEARS) {
                        fees = data.statistics.matter.forecastFeesBilledByCategory.nextThreeYears.forecastFeesBilledGrouping.map((s) => s.fees);
                        grouping = data.statistics.matter.forecastFeesBilledByCategory.nextThreeYears.forecastFeesBilledGrouping.map((s) => s.grouping);
                        format = data.statistics.matter.forecastFeesBilledByCategory.nextThreeYears.forecastFeesBilledGrouping.map((s) => s.format);
                        total = data.statistics.matter.forecastFeesBilledByCategory.nextThreeYears.total;
                    }
                }

                return {
                    ...prevState,
                    fees,
                    grouping,
                    format,
                    total
                };
            });
        }

        // tslint:disable-next-line: align
    }, [state.last, loading, error, data]);

    const onCloseChart = () => {
        if (props.onCloseChart) {
            props.onCloseChart('isForecastFeesBilledByCategoryChartOpen');
        }
    };

    const onToggleVideo = () => {
        setIsVideoModalOpen(!isVideoModalOpen);
    };

    const onChangeRadio = (event: React.ChangeEvent<HTMLInputElement>) => {
        setState((prevState) => {
            return {
                ...prevState,
                // tslint:disable-next-line: radix
                last: parseInt((event.target as HTMLInputElement).value),
            };
        });
    };

    const goToDrilldown = (grouping: string | null, format: string | null, range: PeriodEnum) => {
        if (props.getSelectedItem) {
            const searchCriterias = new Array<SearchCriteria>();

            var forecastDrilldownLabel = '';
    
            if (state.last === PeriodEnum.PREV_MONTH) {
                searchCriterias.push(
                    {
                        property: 'MatterForecastRange',
                        operator: 'month',
                        comparison: 'prev',
                    },
                );
    
                forecastDrilldownLabel = 'Previous Month';
            }
            
            if (state.last === PeriodEnum.MONTH) {
                searchCriterias.push(
                    {
                        property: 'MatterForecastRange',
                        operator: 'month',
                        comparison: 'curr',
                    },
                );
    
                forecastDrilldownLabel = 'This Month';
            }
            
            if (state.last === PeriodEnum.NEXT_MONTH) {
                searchCriterias.push(
                    {
                        property: 'MatterForecastRange',
                        operator: 'month',
                        comparison: 'next',
                    },
                );
    
                forecastDrilldownLabel = 'Next Month';
                
            } else if (state.last === PeriodEnum.NEXT_6_MONTHS) {
                searchCriterias.push(
                    {
                        property: 'MatterForecastRange',
                        operator: 'months',
                        comparison: '',
                        period: '6'
                    },
                );
    
                forecastDrilldownLabel = 'Next 6 Months';

            } else if (state.last === PeriodEnum.NEXT_12_MONTHS) {
                searchCriterias.push(
                    {
                        property: 'MatterForecastRange',
                        operator: 'months',
                        comparison: '',
                        period: '12',
                    },
                );
    
                forecastDrilldownLabel = 'Next 12 Months';

            } else if (state.last === PeriodEnum.NEXT_3_YEARS) {
                searchCriterias.push(
                    {
                        property: 'MatterForecastRange',
                        operator: 'years',
                        comparison: '',
                        period: '3'
                    },
                );
    
                forecastDrilldownLabel = 'Next 3 Years';
            }
    
            if (grouping != null) {
                searchCriterias.push(
                    {
                        property: 'CategoryId',
                        operator: 'eq',
                        comparison: grouping,
                    },
                );
            }

            var drilldownLabel = 'Forecast Fees Billed ' + (format != null ? 'by ' + format : 'by Category ') + ' - ' + forecastDrilldownLabel;
    
            props.getSelectedItem(searchCriterias, true, drilldownLabel, DrillDownType.matterSummaryDrilldown, 'Forecast Fees Billed');
        }
    };

    const handleDrilldownOnClick = () => () => {
        goToDrilldown(null, null, state.last);
    };

    const onPan = (context: { chart: Chart }) => {
        chartRef.current.options.scales.y.min = 0;
        chartRef.current.update();
    };

    const onHover = (event: ChartEvent, elements: ActiveElement[], chart: Chart) => {
        (event?.native?.target as HTMLElement).style.cursor = elements.length > 0 ? 'pointer' : 'auto';
    };

    if (loading || currencySymbolQuery.loading) {
        return (
            <ChartSkeleton variant="bars" />
        );
    }
    return (
        <div className={`${classes.root} ${RV_CHART_CLASS}${isChartDataEmpty(state.fees) ? ' rv-chart--empty' : ''}`}>
            {isVideoModalOpen && props.infoUrl && (
                <VideoModal 
                    open={isVideoModalOpen}
                    onClose={onToggleVideo}
                    url={props.infoUrl}
                />
            )}
            <IconButtonWrapper aria-label="close">
                <div
                    className={`${classes.helpIcon} rv-chart-help`} 
                >
                    <ChartInfo 
                        description={props.description}
                        fontSize={props.showAllRecords ? 'large' : 'medium'}
                        // className={classes.helpIcon}
                    />
                    {props.infoUrl && (
                        <MTooltip title="Learn more">
                            <IconButton className={classes.learnIcon} aria-label="maximize" onClick={onToggleVideo} size={props.showAllRecords ? 'medium' : 'small'}>
                                <VideoLibraryOutlinedIcon />
                            </IconButton>
                        </MTooltip>
                    )}
                </div>
                <IconButton aria-label="close" onClick={onCloseChart} size="small">
                    <CloseIcon className={classes.closeIcon} />
                </IconButton>
            </IconButtonWrapper>
            <div className={classes.titleContainer}>
                <ChartHeading icon={<TimelineOutlinedIcon />} title="Forecast Fees Billed by Category" />
                <div className={`${classes.total} h2`} onClick={handleDrilldownOnClick()}>
                    <AnimatedNumber value={state.total} format={(amount) => `${state.symbol}${formatNumber(amount)}`} />
                </div>
            </div>
            <div className={classes.barContainer}>
                <Bar
                    ref={chartRef}
                    // tslint:disable-next-line: no-any
                    onClick={(event: React.MouseEvent<any>) => {
                        const _data = getElementAtEvent(chartRef.current, event);
                        if (_data.length > 0 && props.getSelectedItem) {
                            const _index = getElementAtEvent(chartRef.current, event)[0].index;
                            const grouping = state.grouping[_index];
                            const format = state.format[_index];

                            goToDrilldown(grouping, format, state.last);
                        }
                    }}
                    data={{
                        labels: getLabels(state.format),
                        datasets: [
                            {
                                fill: false,
                                label: 'Fees Billed',
                                ...rvBarDatasetStyle(RV_CHART_COLORS.primary),
                                data: state.fees,
                                type: 'bar',
                            },
                            // tslint:disable-next-line:no-any
                        ] as any,
                    }}
                    options={{
                        onHover: onHover,
                        indexAxis: 'y' as const,
                        animation: rvChartAnimation(),
                        elements: { bar: { borderWidth: 0 } },
                        responsive: true,
                        maintainAspectRatio: false,
                        scales: {
                            x: {
                                grid: rvValueAxisStyle.grid,
                                // beginAtZero: true,
                                suggestedMax: getSuggestedMaxTick([...state.fees]),
                                beginAtZero: true,
                                ticks: {
                                    ...rvValueAxisStyle.ticks,
                                    autoSkip: false,
                                    callback: function (this: Scale, tickValue: number, index: number, ticks: Tick[]) {
                                        return `${formatNumber(tickValue)}`;
                                    },
                                },
                            },
                            y: {
                                grid: rvCategoryAxisStyle.grid,
                                // min: 0,
                                max: 10, // minimum display
                                ticks: {
                                    ...rvCategoryAxisStyle.ticks,
                                    autoSkip: false
                                }
                            }
                        },
                        plugins: {
                            legend: {
                                position: 'bottom' as const,
                                display: false,
                            },
                            title: {
                                display: false,
                                text: ``,
                            },
                            zoom: {
                                pan: {
                                    enabled: false,
                                    onPan: onPan,
                                },
                                zoom: {
                                    wheel: {
                                        enabled: false,
                                    },
                                },
                                // limits: {
                                //   y: { min: 0, max: 9 },
                                // },
                            },
                            tooltip: {
                                ...rvTooltipStyle,
                                callbacks: {
                                    // tslint:disable-next-line: no-any
                                    label: function (context: any) {
                                        const {
                                            //   dataIndex,
                                            //   label,
                                            formattedValue,
                                            dataset,
                                        } = context;

                                        var currentValue = convertStringNumberToNumber(formattedValue);

                                        return `${dataset.label}: ${state.symbol}${formatNumber(currentValue)}`;
                                    },
                                },
                            },
                        },
                    }}
                />
                {isChartDataEmpty(state.fees) && (
                    <ChartEmptyState title="No data" hint="Nothing to show for this selection." />
                )}
            </div>
            <FormControl component="div">
                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={PeriodEnum.MONTH} control={<Radio color="primary" />} label="This Month" />
                    <FormControlLabel value={PeriodEnum.NEXT_MONTH} control={<Radio color="primary" />} label="Next Month" />
                    <FormControlLabel value={PeriodEnum.PREV_MONTH} control={<Radio color="primary" />} label="Previous Month" />
                </RadioGroup>
                <RadioGroup name="last" value={state.last} onChange={onChangeRadio} className={classes.radioGroup}>
                    <FormControlLabel value={PeriodEnum.NEXT_6_MONTHS} control={<Radio color="primary" />} label="Next 6 Months" />
                    <FormControlLabel value={PeriodEnum.NEXT_12_MONTHS} control={<Radio color="primary" />} label="Next 12 Months" />
                    <FormControlLabel value={PeriodEnum.NEXT_3_YEARS} control={<Radio color="primary" />} label="Next 3 Years" />
                </RadioGroup>
            </FormControl>
            <ChartLimitNote>The top 10 Categories are shown</ChartLimitNote>
        </div>
    );
};

export interface CurrencySymbolData {
    options: Options;
}

export interface Options {
    currencySymbol: CurrencySymbol;
}

export interface CurrencySymbol {
    symbol: string;
}

export interface ForecastFeesBilledByCategoryChartData {
    statistics: Statistics;
}

export interface Statistics {
    matter: Matter;
}

export interface Matter {
    forecastFeesBilledByCategory: ForecastFeesBilledChart;
}

export const CURRENCY_SYMBOL_QUERY = gql`
    query CurrencySymbol {
        options {
            currencySymbol {
                symbol
            }
        }
    }
`;

const ForecastFeesBilledChartQuery = gql`
fragment ForecastFeesBilledRangeFragment on ForecastFeesBilledRange {
    forecastFeesBilledGrouping {
        fees
        format
        grouping
    }
    total
}

query ForecastFeesBilledByCategory($entityType: Int, $entityGuid: String, $responsibility: Int) {
    statistics {
        matter {
            forecastFeesBilledByCategory(entityType: $entityType, entityGuid: $entityGuid, responsibility: $responsibility) {
                currentMonth {
                    ... ForecastFeesBilledRangeFragment
                }
                previousMonth {
                    ... ForecastFeesBilledRangeFragment
                }
                nextMonth {
                    ... ForecastFeesBilledRangeFragment
                }
                nextSixMonths {
                    ... ForecastFeesBilledRangeFragment
                }
                nextTwelveMonths {
                    ... ForecastFeesBilledRangeFragment
                }
                nextThreeYears {
                    ... ForecastFeesBilledRangeFragment
                }
            }
        }
    }
}
`;
